import { PlainExtension, EditorView } from "@remirror/core";

import "./LinkTooltipExtension.css";

export class LinkTooltipExtension extends PlainExtension {
  get name() {
    return "linkTooltip" as const;
  }

  createPlugin() {
    let tooltip: HTMLElement | null = null;
    let hideTimeout: number | null = null;
    let showTimeout: number | null = null;

    const createTooltip = (text: string): HTMLElement => {
      const tooltipElement = document.createElement("div");
      tooltipElement.className = "rich-text-link-tooltip";
      tooltipElement.textContent = text;

      // Add hover listeners to keep tooltip visible when hovering over it
      tooltipElement.addEventListener("mouseenter", () => {
        // Clear any hide timeout when hovering over tooltip
        if (hideTimeout) {
          window.clearTimeout(hideTimeout);
          hideTimeout = null;
        }
      });

      tooltipElement.addEventListener("mouseleave", () => {
        // Hide tooltip when leaving tooltip area
        hideTooltip();
      });

      document.body.appendChild(tooltipElement);

      return tooltipElement;
    };

    // Helper function to check if link text is essentially the same as the URL
    const isLinkTextSameAsUrl = (linkText: string, href: string): boolean => {
      if (!linkText || !href) return false;

      // Normalize both strings for comparison
      const normalizeUrl = (url: string) =>
        url
          .toLowerCase()
          .replace(/^https?:\/\//, "") // Remove protocol
          .replace(/^www\./, "") // Remove www prefix
          .replace(/\/$/, ""); // Remove trailing slash

      const normalizedText = normalizeUrl(linkText);
      const normalizedHref = normalizeUrl(href);

      // Check if they're essentially the same
      return (
        normalizedText === normalizedHref ||
        normalizedText === href.toLowerCase() ||
        linkText.toLowerCase() === href.toLowerCase()
      );
    };

    const showTooltip = (linkElement: HTMLElement, href: string) => {
      // Clear any pending timeouts
      if (hideTimeout) {
        window.clearTimeout(hideTimeout);
        hideTimeout = null;
      }
      if (showTimeout) {
        window.clearTimeout(showTimeout);
        showTimeout = null;
      }

      // If tooltip already exists and shows the same URL, don't recreate
      if (tooltip && tooltip.textContent === href) {
        return;
      }

      // Remove existing tooltip
      hideTooltip();

      // Add design system tooltip delay (300ms for faster response)
      showTimeout = window.setTimeout(() => {
        if (!linkElement.closest("a[href]")) {
          // Link is no longer in DOM or cursor moved away
          return;
        }

        // Get link position first
        const rect = linkElement.getBoundingClientRect();

        // Create tooltip and position it correctly from the start
        tooltip = createTooltip(href);

        // Position tooltip near the link initially (invisible)
        const initialLeft = rect.left + rect.width / 2;
        const initialTop = rect.top - 50; // Approximate height

        tooltip.style.left = `${initialLeft}px`;
        tooltip.style.top = `${initialTop}px`;
        tooltip.style.visibility = "hidden"; // Hide while measuring

        // Get accurate dimensions after text is set and DOM updates
        requestAnimationFrame(() => {
          if (!tooltip) return;

          const tooltipRect = tooltip.getBoundingClientRect();

          // Calculate final position with consistent 20px gap
          const left = rect.left + rect.width / 2 - tooltipRect.width / 2;
          const top = rect.top - tooltipRect.height - 20; // 20px gap (matches Canvas tooltips)

          // Keep tooltip on screen
          const maxLeft = window.innerWidth - tooltipRect.width - 8;
          const minLeft = 8;
          const adjustedLeft = Math.max(minLeft, Math.min(maxLeft, left));

          // If tooltip would appear above viewport, show it below the link instead
          const adjustedTop = top < 8 ? rect.bottom + 20 : top;

          // Set final position and make visible
          tooltip.style.left = `${adjustedLeft}px`;
          tooltip.style.top = `${adjustedTop}px`;
          tooltip.style.visibility = "visible";

          // Now animate in with design system timing
          requestAnimationFrame(() => {
            if (tooltip) {
              tooltip.classList.add("visible");
            }
          });
        });
      }, 300); // Design system tooltip delay
    };

    const hideTooltip = () => {
      if (tooltip) {
        tooltip.classList.remove("visible");

        hideTimeout = window.setTimeout(() => {
          if (tooltip && tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip);
          }
          tooltip = null;
          hideTimeout = null;
        }, 100); // Match design system timing
      }
    };

    return {
      props: {
        handleDOMEvents: {
          mouseover: (_view: EditorView, event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const linkElement = target.closest("a[href]") as HTMLElement;

            if (linkElement && linkElement.getAttribute("href")) {
              const href = linkElement.getAttribute("href")!;
              const linkText = linkElement.textContent?.trim() || "";

              // Only show tooltip for URLs (not anchors) and non-empty links
              if (href && href.length > 3 && !href.startsWith("#")) {
                // Don't show tooltip if link text is already the URL or very similar
                const shouldShowTooltip = !isLinkTextSameAsUrl(linkText, href);

                if (shouldShowTooltip) {
                  showTooltip(linkElement, href);
                }
              }
            }

            return false;
          },
          mouseout: (_view: EditorView, event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const relatedTarget = event.relatedTarget as HTMLElement;

            // Clear any pending show timeout when leaving
            if (showTimeout) {
              window.clearTimeout(showTimeout);
              showTimeout = null;
            }

            // Only hide if we're truly leaving the link AND not entering the tooltip
            if (
              target.closest("a[href]") &&
              !relatedTarget?.closest("a[href]") &&
              !relatedTarget?.classList.contains("rich-text-link-tooltip")
            ) {
              // Add small delay to allow moving to tooltip
              hideTimeout = window.setTimeout(() => {
                hideTooltip();
              }, 50);
            }

            return false;
          },
        },
      },
    };
  }
}
